import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';

import amplitude from 'amplitude-js';

import Box from '@material-ui/core/Box';
import Container from '@material-ui/core/Container';
import SvgIcon from '@material-ui/core/SvgIcon';

import { Formik } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import PasswordStrengthBar from 'react-password-strength-bar';
import { isMobile } from 'react-device-detect';
import ResetPasswordIcon from '../../../assets/images/icons/reset-password.svg';
import NavigationBar from '../../../components/NavigationBar';
import TextField from '../../../components/TextField';
import Button from '../../../components/Button';

import MessageModal from '../../../modals/message';

import AuthActions from '../../../redux/auth/actions';

import styles from './styles.module.sass';

const ResetPassword = ({ t, history, match }) => {
  const validationSchema = Yup.object({
    password: Yup.string().required(t('common.requiredField')).min(8, t('common.minPassword')),

    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], t('common.passwordMustMatch'))
      .required(t('common.requiredField')),
  });

  const dispatch = useDispatch();

  const { resetPasswordLoading, resetPasswordError, resetPasswordCalled } = useSelector(
    (state) => state.auth
  );

  const [minHeight, setMinHeight] = useState(0);

  const formBox = React.createRef();

  useEffect(() => {
    setMinHeight(formBox?.current?.clientHeight + (isMobile ? 0 : 406));
  }, [formBox]);

  useEffect(() => {
    if (process.env.REACT_APP_AMPLITUDE !== 'disable') {
      amplitude.getInstance().logEvent(`page${window.location.pathname}`);
    }
  }, []);

  function handleSubmit(values) {
    dispatch(AuthActions.resetPassword(match.params.code, values.password));
  }

  return (
    <Box className={styles.container} style={{ minHeight }}>
      <NavigationBar
        title={t('forgotPassword.title')}
        hasBackButton
        hasMenuButton={!isMobile}
        hideFirstBar={!isMobile}
        customBack={() => {
          dispatch(AuthActions.clearResetPassword());
          history.replace('/login');
        }}
        history={history}
      />
      {resetPasswordCalled && resetPasswordLoading === false && resetPasswordError === null && (
        <MessageModal
          title={t('common.success')}
          message={t('resetPassword.success')}
          primaryAction={t('common.close')}
          primaryEvent={() => {
            dispatch(AuthActions.clearResetPassword());
            history.replace('/login');
          }}
        />
      )}
      <Container maxWidth="md" className={styles.content} ref={formBox}>
        <Box className={styles.icon}>
          <SvgIcon component={ResetPasswordIcon} viewBox="0 0 82 82" />
        </Box>
        <Box className={styles.container_step_description}>
          <Box className="title" style={{ marginTop: 20 }}>
            {t('forgotPassword.title')}
          </Box>
          <Box className="subtitle" style={{ marginTop: 20 }}>
            {t('resetPassword.description')}
          </Box>
        </Box>
        <Box className={styles.container_inputs}>
          <Formik
            initialValues={{ password: '', confirmPassword: '' }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              handleSubmit(values);
            }}
          >
            {({ values, errors, touched, handleChange, handleSubmit }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Box className={styles.container_inputs}>
                  <Box className={styles.container_input}>
                    <TextField
                      value={values.password}
                      onChange={handleChange}
                      className={styles.input}
                      helperText={errors.password && touched.password && errors.password}
                      name="password"
                      type="password"
                      required
                      placeholder={
                        t('common.password').charAt(0).toUpperCase() + t('password').slice(1)
                      }
                      label={t('common.password').toUpperCase()}
                    />
                  </Box>
                  <PasswordStrengthBar
                    style={{ marginLeft: 0, marginTop: -2 }}
                    password={values.password}
                    minLength={8}
                  />
                  <Box className={styles.container_input}>
                    <TextField
                      value={values.confirmPassword}
                      onChange={handleChange}
                      className={styles.input}
                      helperText={
                        errors.confirmPassword && touched.confirmPassword && errors.confirmPassword
                      }
                      name="confirmPassword"
                      type="password"
                      required
                      placeholder={
                        t('common.confirmPassword').charAt(0).toUpperCase() +
                        t('confirmPassword').slice(1)
                      }
                      label={t('common.confirmPassword').toUpperCase()}
                    />
                  </Box>
                </Box>
                {resetPasswordError && (
                  <Box className="error" style={{ marginTop: 15 }}>
                    {resetPasswordError?.userMessage}
                  </Box>
                )}

                <Box className={styles.container_submit}>
                  <Button
                    className={styles.button}
                    type="submit"
                    loading={resetPasswordLoading}
                    disabled={resetPasswordLoading}
                    data-info-submit="Submit"
                    minWidth={130}
                    maxWidth={270}
                  >
                    {t('common.reset')}
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      </Container>
    </Box>
  );
};

export default withTranslation()(ResetPassword);
